import { mergeMaps } from '../utils';
import FileSaver from 'file-saver';
import { invoke } from '@tauri-apps/api/core'
import { DEFAULT_CONNECTION_CREATION } from '../../draw/connectionCreationDefaults.js'

export const defaultSettings = {
    mqtt: {
        protocol: 'mqtt',
        host: 'mqts://localhost:9001',
        port: '1883',
        id: "haspdesigner",
        username: 'username',
        password: 'password'
    },
    hass: {
        protocol: 'https',
        host: 'hass.local',
        port: 8443,
        token: 'Generate your token',
        sftp_port: 22,
        sftp_user: 'root',
        sftp_pass: '',
        sftp_path: '/config/',
    },
    ui: {
        main_color: '#092e53',
        canvas_color: '#cccccc',
        connection_spacing: 12,
        connection_max_speed: 120,
        object_defaults: {
            connection: { ...DEFAULT_CONNECTION_CREATION },
        },
        show_hasp_id: false,
        show_object_details: true,
        show_doc_icon: true,
        zoom_sensitivity: 1.0,
        ruler_scale: 10,
        page0_overlay: true,
        sticky_guidelines: true,
        enable_history: true,
    },
    plate: {
        id: 0,
        name: 'plate',
        host: 'plate.local',
        hasp_theme: 'materialdark', //to plateconfig
        primary_color: '#42a5f6ff', //to plateconfig
        secondary_color: '#fb8c00ff', //to plateconfig
        user: '',
        pass: '',
        size_name: 'small_landscape',
        width: 320,
        height: 240,
        shape: 'rectangular',
        native: false,
        http_port: 8080,
        telnet_port: 2323,
        font_sizes: '',
    },
    github: {
        token: '',
        owner: '',
        repo: '',
        branch: 'main',
        folder: 'designs',
    },
    tauri: false
}

export const haspThemes = [
    { name: 'hasplight', title: 'Hasp Light', option: 1 },
    { name: 'haspdark', title: 'Hasp Dark', option: 2 },
    { name: 'mono', title: 'Mono', option: 3 },
    { name: 'materiallight', title: 'Material Light', option: 4 },
    { name: 'materialdark', title: 'Material Dark', option: 5 },
];

const SETTINGS_NAME = 'haspdesigner_settings';
const PLATES_NAME = 'plates_settings';
const SAVE_FILE_NAME = 'haspdesigner_settings';
const GITHUB_SETTINGS_KEY = 'haspdesigner_github_settings';

function loadGitHubSettings() {
    try {
        const stored = localStorage.getItem(GITHUB_SETTINGS_KEY);
        if (stored) return { ...defaultSettings.github, ...JSON.parse(stored) };
    } catch (e) { /* ignore */ }
    return { ...defaultSettings.github };
}

function saveGitHubSettings(github) {
    try {
        localStorage.setItem(GITHUB_SETTINGS_KEY, JSON.stringify(github));
    } catch (e) {
        console.error('Failed to save GitHub settings:', e);
    }
}

export async function loadSettings() {
    // localStorage.removeItem('haspdesigner_settings'); //for debugging

    //from the default settings we fetch the stored settings
    var appSettings = { ...defaultSettings };

    if (window.__TAURI__) {
        for (const [key, value] of Object.entries(appSettings.mqtt)) {
            const category = 'mqtt';
            let valueToStore = typeof value === 'number' ? value.toString() : (typeof value === "string" ? value : JSON.stringify(value));
            const valueStr = await invoke("get_setting_default_command", { key, value: valueToStore, category });
            appSettings.mqtt[key] = JSON.parse(valueStr);
        }
        for (const [key, value] of Object.entries(appSettings.hass)) {
            const category = 'hass';
            let valueToStore = typeof value === 'number' ? value.toString() : (typeof value === "string" ? value : JSON.stringify(value));
            const valueStr = await invoke("get_setting_default_command", { key, value: valueToStore, category });
            appSettings.hass[key] = JSON.parse(valueStr);
        }
        for (const [key, value] of Object.entries(appSettings.plate)) {
            const category = 'plate';
            let valueToStore = typeof value === 'number' ? value.toString() : (typeof value === "string" ? value : JSON.stringify(value));
            const valueStr = await invoke("get_setting_default_command", { key, category, value: valueToStore });
            appSettings.plate[key] = JSON.parse(valueStr);
        }
        for (const [key, value] of Object.entries(appSettings.ui)) {
            const category = 'ui';
            let valueToStore = typeof value === 'number' ? value.toString() : (typeof value === "string" ? value : JSON.stringify(value));
            const valueStr = await invoke("get_setting_default_command", { key, category, value: valueToStore });
            appSettings.ui[key] = JSON.parse(valueStr);
        }
    } else {
        try {
            const storedSettings = localStorage.getItem(SETTINGS_NAME);
            if (storedSettings) {
                const sett = JSON.parse(storedSettings);
                appSettings = mergeMaps(sett, defaultSettings);
            }
        } catch (e) {
            appSettings = { ...defaultSettings };
            console.error('Failed to load settings from localStorage:', e);
        }
    }

    // GitHub settings always use their own localStorage key (works in both web and Tauri modes)
    appSettings.github = loadGitHubSettings();

    // Check if we are running in Tauri
    appSettings.tauri = '__TAURI__' in window;
    return appSettings;
}

export async function saveAllSettings(appSettings, type) {
    // GitHub settings always saved to their own key regardless of mode
    if (type === 'github' || type === 'all') {
        saveGitHubSettings(appSettings.github);
    }

    if (window.__TAURI__) {
        //we update by type to speed things up
        switch (type) {
            case 'mqtt':
                callSaveSettingsCommand('mqtt', appSettings.mqtt);
                break;
            case 'hass':
                callSaveSettingsCommand('hass', appSettings.hass);
                break;
            case 'active-plate':
                callSaveSettingsCommand('plate', appSettings.plate);
                break;
            case 'ui':
                callSaveSettingsCommand('ui', appSettings.ui);
                break;
            case 'github':
                break; // already handled above
            case 'all':
                callSaveSettingsCommand('mqtt', appSettings.mqtt);
                callSaveSettingsCommand('hass', appSettings.hass);
                callSaveSettingsCommand('plate', appSettings.plate);
                callSaveSettingsCommand('ui', appSettings.ui);
                break;
            default:
                console.error("Unknown setting type:", type);
        }

    } else {
        localStorage.setItem(SETTINGS_NAME, JSON.stringify(appSettings));
    }
}

async function callSaveSettingsCommand(category, settings) {
    for (const [key, svalue] of Object.entries(settings)) {
        let value = svalue;
        if(typeof value === 'number') value = value.toString();
        if (key === 'id') continue; //skip id, it is not needed to be saved
        if (category === 'ui') {
            let valueToStore = typeof value === "string" ? value : JSON.stringify(value);
            const setting = { key, category, value: valueToStore };
            await invoke("set_setting_command", { setting });
        } else {
            const setting = { key, value: typeof value === 'string' ? value : JSON.stringify(value), category };
            // console.log("setting", setting);
            await invoke("set_setting_command", { setting });
        }
    }
}


export async function readSettingsFromFile(file) {

    var appSettings = { ...defaultSettings };
    if (file) {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.onload = (e) => {
                try {
                    const newSettings = JSON.parse(e.target.result);
                    appSettings = newSettings;
                    resolve(appSettings);
                } catch (error) {
                    console.error('Error parsing JSON file:', error);
                    reject(error);
                }
            };

            fileReader.onerror = (error) => {
                console.error('Error reading file:', error);
                reject(error);
            };

            fileReader.readAsText(file);
        });
    } else {
        return appSettings;
    }
}

export async function writeSettingsToFile() {
    try {
        const storedSettings = localStorage.getItem(SETTINGS_NAME);
        if (storedSettings) {
            const sett = JSON.parse(storedSettings);

            var blob = new Blob([JSON.stringify(sett, null, 4)], { type: 'text/plain;charset=utf-8' });
            FileSaver.saveAs(blob, SAVE_FILE_NAME + '.json');
        }

    } catch (e) {
        console.error('Failed to save settings:', e);
    }
}

export async function addPlate(plate) {
    if (window.__TAURI__) {
        invoke('add_plate_command', { plate });
    }
    const plates = getStoredPlates();
    plates.push(plate);
    localStorage.setItem(PLATES_NAME, JSON.stringify(plates));
}

export async function deletePlate(index, plate) {
    if (window.__TAURI__) {
        const plate_id = plate.id;
        invoke('delete_plate_command', { id: plate_id });
    }
    const plates = getStoredPlates();
    plates.splice(index, 1);
    localStorage.setItem(PLATES_NAME, JSON.stringify(plates));
}

export async function updatePlate(index, plate) {
    if (window.__TAURI__) {
        const id = plate.id;
        await invoke('update_plate_command', { id, plate });
    }
    const plates = getStoredPlates();
    const existing = plates.findIndex(p => p.id === plate.id);
    if (existing >= 0) plates[existing] = plate;
    else plates[index] = plate;
    localStorage.setItem(PLATES_NAME, JSON.stringify(plates));
}

export async function saveAllPlates(plates) {
    if (window.__TAURI__) {
        // Tauri backend manages its own storage; localStorage is always kept in sync.
    }
    localStorage.setItem(PLATES_NAME, JSON.stringify(plates));
}

function getStoredPlates() {
    let plates = new Array;
    try {
        const storedPlates = localStorage.getItem(PLATES_NAME);
        if (storedPlates) {
            const plates = JSON.parse(storedPlates);
            return plates;
        }
        return plates;
    } catch (e) {
        return plates;
    }
}

export async function getAllPlates() {
    if (window.__TAURI__) {
        const plates = await invoke("get_plates_command");
        return plates;
    } else {
        return getStoredPlates();
    }
}
